import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { IZoneRepository, CreateZoneData, UpdateZoneData, ZoneFilters } from '../interfaces/IZoneRepository';
import { Zone } from '../../types';

export class FirebaseZoneRepository implements IZoneRepository {
  private readonly collectionName = 'zones';

  async create(data: CreateZoneData): Promise<Zone> {
    try {
      // Construire les données en excluant les valeurs undefined
      const zoneData: any = {
        name: data.name,
        coordinates: data.coordinates,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Ajouter les contacts seulement s'ils ne sont pas undefined
      if (data.contacts !== undefined && data.contacts.length > 0) {
        zoneData.contacts = data.contacts;
      }
      
      // Ajouter les gestionnaires seulement s'ils ne sont pas undefined
      if (data.managers !== undefined && data.managers.length > 0) {
        zoneData.managers = data.managers;
      }

      const docRef = await addDoc(collection(db!, this.collectionName), zoneData);

      return {
        id: docRef.id,
        name: data.name,
        coordinates: data.coordinates,
        contacts: data.contacts,
        managers: data.managers,
      };
    } catch (error) {
      console.error('Erreur lors de la création de la zone:', error);
      throw new Error('Erreur lors de la création de la zone');
    }
  }

  async getById(id: string): Promise<Zone | null> {
    try {
      const docRef = doc(db!, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.firestoreToZone(id, docSnap.data());
    } catch (error) {
      console.error('Erreur lors de la récupération de la zone:', error);
      return null;
    }
  }

  async getAll(filters?: ZoneFilters): Promise<Zone[]> {
    try {
      let q = query(
        collection(db!, this.collectionName),
        orderBy('name', 'asc')
      );


      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => 
        this.firestoreToZone(doc.id, doc.data())
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des zones:', error);
      return [];
    }
  }

  async update(id: string, data: UpdateZoneData): Promise<Zone> {
    try {
      const docRef = doc(db!, this.collectionName, id);
      
      // Filtrer les valeurs undefined
      const updateData: any = {
        updatedAt: Timestamp.now(),
      };

      // Ajouter seulement les champs qui ne sont pas undefined
      Object.keys(data).forEach(key => {
        const value = (data as any)[key];
        if (value !== undefined) {
          updateData[key] = value;
        }
      });

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      if (!updatedDoc.exists()) {
        throw new Error('Zone non trouvée après mise à jour');
      }

      return this.firestoreToZone(id, updatedDoc.data());
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la zone:', error);
      throw new Error('Erreur lors de la mise à jour de la zone');
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(db!, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Erreur lors de la suppression de la zone:', error);
      throw new Error('Erreur lors de la suppression de la zone');
    }
  }


  async getZonesContainingPoint(latitude: number, longitude: number): Promise<Zone[]> {
    try {
      // Récupérer toutes les zones et filtrer côté client
      // (Firestore ne supporte pas les requêtes géospatiales complexes nativement)
      const q = query(collection(db!, this.collectionName));
      const querySnapshot = await getDocs(q);
      
      const allZones = querySnapshot.docs.map(doc => 
        this.firestoreToZone(doc.id, doc.data())
      );

      return allZones.filter(zone => 
        this.isPointInPolygon(
          { latitude, longitude },
          zone.coordinates
        )
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des zones contenant le point:', error);
      return [];
    }
  }

  onZonesChange(callback: (zones: Zone[]) => void, filters?: ZoneFilters): () => void {
    let q = query(
      collection(db!, this.collectionName),
      orderBy('name', 'asc')
    );


    if (filters?.limit) {
      q = query(q, limit(filters.limit));
    }

    return onSnapshot(q, (querySnapshot) => {
      const zones = querySnapshot.docs.map(doc => 
        this.firestoreToZone(doc.id, doc.data())
      );
      callback(zones);
    }, (error) => {
      console.error('Erreur dans l\'écoute des changements de zones:', error);
    });
  }

  private isPointInPolygon(
    point: { latitude: number; longitude: number },
    polygon: Array<{ latitude: number; longitude: number }>
  ): boolean {
    const x = point.longitude;
    const y = point.latitude;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].longitude;
      const yi = polygon[i].latitude;
      const xj = polygon[j].longitude;
      const yj = polygon[j].latitude;

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }

  private firestoreToZone(id: string, data: any): Zone {
    return {
      id,
      name: data.name,
      coordinates: data.coordinates,
      contacts: data.contacts || [],
      managers: data.managers || [],
    };
  }
}